import { Modal, ModalCloseButton, ModalContent, ModalOverlay } from '@chakra-ui/modal';
import { Button, Input } from '@sketch-note/design-system/src/components';
import { Navigation } from '@sketch-note/design-system/src/shared/site-settings';
import cn from 'classnames';
import Link from 'next/link';
import * as React from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { API, endpoints } from 'src/api';
import { AuthFlowLayout } from 'src/layouts/auth-flow';
const { useState } = React;

export function ErrorContent({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <div
      className={cn({
        'text-center py-16 bg-red-100 text-red-900 font-medium text-sm rounded': true,
        [className || '']: !!className,
      })}
    >
      {children}
    </div>
  );
}

function ForgotPassword(): JSX.Element {
  const { register, handleSubmit, control, setError } = useForm();
  const { errors } = useFormState({ control });

  const [email, setEmail] = useState('');
  const [magicLinkConfirmation, setMagicLinkConfirmation] = useState(false);
  const [invalidEmail, setInvalidEmail] = useState<boolean>(false);

  const handlePasswordReset = async (values) => {
    setEmail(values.email);
    const response = await API.post(endpoints.forgotPassword, {
      email: values.email,
    });

    if (response?.data.success) {
      setInvalidEmail(false);
      setMagicLinkConfirmation(true);
    } else {
      setInvalidEmail(true);
    }
  };

  return (
    <div className="w-336 mx-auto">
      <h2 className="mb-14 leading-40 text-4xl">Reset your password</h2>
      <p className="mb-16 text-gray-600 text-sm">
        Enter your eamil address and we’ll send you a link to reset your password
      </p>
      {invalidEmail && (
        <ErrorContent className="mb-16">
          Hmm, this email doesn’t seem to belong to an active Sketchnote account. Try again!
        </ErrorContent>
      )}
      <form onSubmit={handleSubmit(handlePasswordReset)}>
        <Input
          placeholder="Email address"
          field="text"
          textSecClassName="mb-16"
          inputProps={{
            ...register('email', {
              required: true,
            }),
          }}
          status={errors.email ? 'danger' : undefined}
          caption={errors?.email?.message}
        />

        <Button status="primary" className="w-full justify-center mb-34" size="lg" type="submit">
          Send password reset link
        </Button>
      </form>

      <Button
        onClick={() => {
          location.reload();
        }}
        status="ghost"
        className="pl-0 mb-58 text-sm"
      >
        Back to sign in
      </Button>

      <p className="text-gray-500 mb-34 text-center text-xs font-medium">
        <Link href="https://sketchnote.typeform.com/contact-us">Contact support</Link>
      </p>

      <Modal
        isOpen={magicLinkConfirmation}
        onClose={() => {
          setMagicLinkConfirmation(false);
        }}
      >
        <ModalOverlay />

        <ModalContent
          style={{ maxWidth: '38rem', padding: '32px', marginTop: '30rem', borderRadius: '8px' }}
        >
          <p className="mb-16 font-medium text-2xl">You&apos;ve got mail!</p>
          <p className="mb-32 text-sm text-gray-600">
            <ModalCloseButton />
            We&apos;ve sent a login link to {email}.<br />
            Click on it to start using Sketchnote!
          </p>
          <button
            className="self-end bg-blue-500 text-center text-white py-13 px-24 rounded-sm font-medium w-86"
            onClick={() => {
              setMagicLinkConfirmation(false);
            }}
          >
            Okay
          </button>
        </ModalContent>
      </Modal>
    </div>
  );
}

ForgotPassword.layout = AuthFlowLayout;

export default ForgotPassword;
